import React, { useEffect, useRef } from 'react';
import { ArrowDown, ArrowUp, Trash2 } from 'lucide-react';
import BrandSelect from 'features/playground/components/BrandSelect';
import {
  ROLES, messageText, setMessageText,
} from 'features/prompts/lib/promptDraft';

/** `system` → `System`. The stored value stays lower-case; this is display only. */
const titleCase = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const controlCls = 'h-8 rounded-md border border-[color:var(--border-default)] bg-transparent px-2.5 text-[12px] text-[color:var(--text-primary)] focus:outline-none focus:border-[color:var(--border-strong)] focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]';

// Any {{ name }} token — highlighted so the author can see a variable was
// detected. Matches the engine's accepted name shape (letters, digits, _ $ .).
const TOKEN_RE = /\{\{\s*[A-Za-z_$][A-Za-z0-9_$.]*\s*\}\}/g;

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Escaped text with {{tokens}} wrapped in a coloured span. Trailing newline
 * keeps the backdrop's height in step with the textarea's last line.
 *
 * The backdrop sits UNDER a transparent-text textarea, so every glyph has to
 * land on the same pixel as the real one. That rules out padding and a real
 * font-weight change (bold mono is not guaranteed to keep the advance width) —
 * so weight is faked with a hairline text-shadow, which costs no metrics, and
 * the chip background is drawn tight to the glyphs.
 *
 * Colour is the brand orange pulled ~30% toward the body text: enough to stay
 * recognisably "a variable" without the full #ff6d29 glare against dark code.
 */
const TOKEN_STYLE = [
  'color:color-mix(in srgb, var(--trodo-primary) 70%, var(--text-primary))',
  'background:rgb(var(--trodo-primary-rgb) / 0.14)',
  'border-radius:3px',
  'text-shadow:0 0 0.4px currentColor',
].join(';');

function highlight(text) {
  const escaped = escapeHtml(text || '');
  return `${escaped.replace(
    TOKEN_RE,
    (m) => `<span style="${TOKEN_STYLE}">${m}</span>`,
  )}\n`;
}

/**
 * A textarea that shows `{{variables}}` in the brand colour as you type.
 *
 * A plain textarea can't style its own text, so we render a highlighted backdrop
 * behind a transparent-text textarea and keep their scroll in sync. Both layers
 * share identical typography + padding, so the coloured tokens sit exactly under
 * the real characters.
 *
 * Typing the second `{` of a variable auto-inserts the closing `}}` and parks the
 * caret between them — but only at that moment. If the author deletes the added
 * braces they stay gone; we never re-add, in case they wanted a bare `{{`.
 */
function HighlightedTextarea({ value, onChange, disabled, placeholder, rows }) {
  const taRef = useRef(null);
  const backRef = useRef(null);
  // A caret position to restore after a programmatic edit re-renders the value.
  const pendingCaret = useRef(null);

  useEffect(() => {
    if (pendingCaret.current != null && taRef.current) {
      const pos = pendingCaret.current;
      pendingCaret.current = null;
      taRef.current.selectionStart = pos;
      taRef.current.selectionEnd = pos;
    }
  }, [value]);

  const syncScroll = () => {
    if (backRef.current && taRef.current) {
      backRef.current.scrollTop = taRef.current.scrollTop;
      backRef.current.scrollLeft = taRef.current.scrollLeft;
    }
  };

  const handleKeyDown = (e) => {
    const ta = taRef.current;
    if (!ta) return;
    const s = ta.selectionStart;
    const eSel = ta.selectionEnd;
    const v = ta.value;

    // Auto-close a variable: the 2nd "{" inserts "}}" and drops the caret inside,
    // giving {{|}}. Only when it forms exactly "{{" (not a 3rd brace) and "}}"
    // isn't already there, and never across a selection.
    if (e.key === '{' && s === eSel && v[s - 1] === '{' && v[s - 2] !== '{' && v.slice(s, s + 2) !== '}}') {
      e.preventDefault();
      const next = `${v.slice(0, s)}{}}${v.slice(s)}`;
      pendingCaret.current = s + 1;
      onChange(next);
      return;
    }

    // Type over an auto-added closing brace rather than stacking another.
    if (e.key === '}' && s === eSel && v[s] === '}') {
      e.preventDefault();
      ta.selectionStart = s + 1;
      ta.selectionEnd = s + 1;
    }
  };

  const shared = 'px-3 py-2 font-mono text-[12.5px] leading-relaxed whitespace-pre-wrap break-words';

  return (
    <div className="relative">
      <div
        ref={backRef}
        aria-hidden
        className={`${shared} pointer-events-none absolute inset-0 overflow-auto text-[color:var(--text-primary)]`}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: highlight(value) }}
      />
      <textarea
        ref={taRef}
        rows={rows}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onScroll={syncScroll}
        placeholder={placeholder}
        spellCheck={false}
        className={`${shared} relative block w-full resize-y bg-transparent text-transparent caret-[color:var(--text-primary)] placeholder:text-[color:var(--text-disabled)] focus:outline-none`}
      />
    </div>
  );
}

function IconBtn({ onClick, disabled, title, children }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className="rounded p-1 text-[color:var(--text-muted)] hover:bg-[color:var(--surface-hover)] hover:text-[color:var(--text-primary)] disabled:opacity-30 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  );
}

/**
 * The structured message editor.
 *
 * Roles are plain (no colour coding). Every role — `system`, `user`,
 * `assistant` — can appear any number of times, in any order (the server allows
 * the same). Each message is one compact card — the role row and the text share
 * a surface, no divider — and row actions reveal on hover.
 *
 * Adding messages (and placeholders) is driven from the toolbar above, in the
 * parent — this component only renders and edits the existing list.
 */
export default function MessagesEditor({ messages, onChange, disabled }) {
  const set = (i, next) => onChange(messages.map((m, j) => (j === i ? next : m)));
  const remove = (i) => onChange(messages.filter((_, j) => j !== i));

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= messages.length) return;
    const next = [...messages];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  // Hover-revealed row actions. `group-focus-within` keeps them up while the
  // textarea is focused, so keyboard users aren't left guessing.
  const actionsCls = 'flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100';

  return (
    <div className="flex flex-col gap-2">
      {messages.map((m, i) => {
        if (m.type === 'placeholder') {
          return (
            <div
              key={`ph-${i}`}
              className="group flex items-center gap-2 rounded-md border border-dashed border-[color:var(--border-default)] px-3 py-2"
            >
              <span className="px-1 py-0.5 text-[12.5px] font-medium text-[color:var(--text-primary)]">
                Placeholder
              </span>
              <input
                className={`${controlCls} flex-1 font-mono`}
                value={m.name}
                disabled={disabled}
                onChange={(e) => set(i, { ...m, name: e.target.value.trim() })}
                placeholder="chat_history"
              />
              <div className={actionsCls}>
                <IconBtn title="Move up" onClick={() => move(i, -1)} disabled={disabled || i === 0}>
                  <ArrowUp size={13} />
                </IconBtn>
                <IconBtn title="Move down" onClick={() => move(i, 1)} disabled={disabled || i === messages.length - 1}>
                  <ArrowDown size={13} />
                </IconBtn>
                <IconBtn title="Remove" onClick={() => remove(i)} disabled={disabled}>
                  <Trash2 size={13} />
                </IconBtn>
              </div>
            </div>
          );
        }

        return (
          <div
            key={`m-${i}`}
            className="group rounded-md border border-[color:var(--border-default)] focus-within:border-[color:var(--border-strong)]"
          >
            <div className="flex items-center gap-2 px-2 pt-1.5 pb-0.5">
              <BrandSelect
                value={m.role}
                disabled={disabled}
                variant="bare"
                minWidth={0}
                options={ROLES.map((r) => ({ value: r, label: titleCase(r) }))}
                onChange={(r) => set(i, { ...m, role: r })}
              />
              <div className="flex-1" />
              <div className={actionsCls}>
                <IconBtn title="Move up" onClick={() => move(i, -1)} disabled={disabled || i === 0}>
                  <ArrowUp size={13} />
                </IconBtn>
                <IconBtn title="Move down" onClick={() => move(i, 1)} disabled={disabled || i === messages.length - 1}>
                  <ArrowDown size={13} />
                </IconBtn>
                <IconBtn title="Remove" onClick={() => remove(i)} disabled={disabled || messages.length === 1}>
                  <Trash2 size={13} />
                </IconBtn>
              </div>
            </div>
            <HighlightedTextarea
              rows={m.role === 'system' ? 6 : 3}
              disabled={disabled}
              value={messageText(m)}
              onChange={(text) => set(i, setMessageText(m, text))}
              placeholder={m.role === 'system' ? 'You are a {{persona}}…' : '{{question}}'}
            />
          </div>
        );
      })}
    </div>
  );
}
